/**
 * 重複名チェック用のユーティリティ関数群
 * データセット、モデル、レポートの名前重複検出と連番生成を提供
 */

import { MIDASProject } from '../../types/project.types';
import type { LayoutState } from '../../types/pane.types';
import { checkDatasetDependencies as checkDeps, DatasetDependencies } from './dependencyChecker';

export type DuplicateAction = 'rename' | 'increment' | 'replace';

export interface DuplicateCheckResult {
  isDuplicate: boolean;
  suggestedName?: string;
  duplicateType: 'dataset' | 'model' | 'report';
  originalName: string;
  // 依存関係情報（データセットの場合のみ）
  dependencies?: DatasetDependencies;
  canReplace?: boolean; // 依存関係がない場合にのみtrue
}

/**
 * データセット名の重複をチェック
 * @param name チェックする名前
 * @param project プロジェクト
 * @param layoutState オプション: 現在のレイアウト状態（タブの依存チェックに使用）
 */
export function checkDatasetNameDuplicate(
  name: string,
  project: MIDASProject,
  layoutState?: LayoutState
): DuplicateCheckResult {
  const isDuplicate = Object.values(project.datasets).some(
    dataset => dataset.name === name
  );

  if (!isDuplicate) {
    return {
      isDuplicate: false,
      duplicateType: 'dataset',
      originalName: name
    };
  }

  // 重複がある場合、依存関係をチェック
  const existingDataset = Object.values(project.datasets).find(
    dataset => dataset.name === name
  );

  if (!existingDataset) {
    return {
      isDuplicate: true,
      suggestedName: generateIncrementedName(name, getDatasetNames(project)),
      duplicateType: 'dataset',
      originalName: name
    };
  }

  const dependencies = checkDeps(project, existingDataset.id, layoutState);

  return {
    isDuplicate: true,
    suggestedName: generateIncrementedName(name, getDatasetNames(project)),
    duplicateType: 'dataset',
    originalName: name,
    dependencies,
    canReplace: !dependencies.hasAnyDependencies
  };
}

/**
 * モデル名の重複をチェック
 */
export function checkModelNameDuplicate(
  name: string,
  project: MIDASProject
): DuplicateCheckResult {
  const models = project.models;
  const isDuplicate = Object.values(models).some(
    model => model.name === name
  );

  return {
    isDuplicate,
    suggestedName: isDuplicate ? generateIncrementedName(name, getModelNames(project)) : undefined,
    duplicateType: 'model',
    originalName: name
  };
}

/**
 * レポート名の重複をチェック
 */
export function checkReportNameDuplicate(
  name: string,
  project: MIDASProject
): DuplicateCheckResult {
  const reports = project.reports || {};
  const isDuplicate = Object.values(reports).some(
    report => report.name === name
  );

  return {
    isDuplicate,
    suggestedName: isDuplicate ? generateIncrementedName(name, getReportNames(project)) : undefined,
    duplicateType: 'report',
    originalName: name
  };
}

/**
 * 連番付きの名前を生成する
 * 例: "Dataset" -> "Dataset (1)", "Dataset (2)", ...
 */
export function generateIncrementedName(baseName: string, existingNames: string[]): string {
  if (!existingNames.includes(baseName)) {
    return baseName;
  }

  let counter = 1;
  let candidateName: string;

  do {
    candidateName = `${baseName} (${counter})`;
    counter++;
  } while (existingNames.includes(candidateName));

  return candidateName;
}

/**
 * プロジェクト内の全データセット名を取得
 */
function getDatasetNames(project: MIDASProject): string[] {
  return Object.values(project.datasets).map(dataset => dataset.name);
}

/**
 * プロジェクト内の全モデル名を取得
 */
function getModelNames(project: MIDASProject): string[] {
  const models = project.models;
  return Object.values(models).map(model => model.name);
}

/**
 * プロジェクト内の全レポート名を取得
 */
function getReportNames(project: MIDASProject): string[] {
  const reports = project.reports || {};
  return Object.values(reports).map(report => report.name);
}

/**
 * 統一的な重複チェック関数
 * 型によって適切なチェック関数を呼び出す
 */
export function checkNameDuplicate(
  name: string,
  type: 'dataset' | 'model' | 'report',
  project: MIDASProject
): DuplicateCheckResult {
  switch (type) {
    case 'dataset':
      return checkDatasetNameDuplicate(name, project);
    case 'model':
      return checkModelNameDuplicate(name, project);
    case 'report':
      return checkReportNameDuplicate(name, project);
    default:
      throw new Error(`Unsupported type: ${type}`);
  }
}